/**
 * /downhole/components — the tool register (fieldiq-api#882
 * `GET /downhole/components`, `/components/alerts`, `/dashboard`).
 *
 * WRITES (api#900): Add Component, on the band and in the empty register, and
 * Import CSV. Admin, office_staff and dispatcher only — the API's inventory
 * roles — so a field hand sees neither. The dialogs are ComponentDialogs.tsx.
 * (The unrouted screen's old Add dialog posted `connection_type`, a vestigial
 * column the rebuild doc says not to revive, and wrote a blank wear limit as 0;
 * neither survives.)
 *
 * FILTERING IS THE SERVER'S. `status`, `category` and `q` are query params the
 * API reads; the list it returns is the answer. The chip counts are
 * `/dashboard`'s `components_by_status` — the API's figure for the whole
 * register, so a chip keeps its count while another chip is selected. A
 * tally of the loaded rows would be a second answer, and wrong under a filter.
 *
 * UNITS through `formatDh` and the tenant's `meta.unit_system`: OD in in/mm,
 * length in ft/m. Never a bare `"`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ChevronRight, FileUp, Loader2, Plus, Search, Wrench } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeComponentAlerts, useDownholeComponents, useDownholeDashboard } from '../../hooks/useDownhole';
import { DH_CATEGORY_LABEL, DH_COMPONENT_STATUS_SKIN, dhSkin } from '../../lib/downhole-labels';
import { canManageDownholeInventory } from '../../lib/downhole-roles';
import { formatDh } from '../../lib/downhole-units';
import { fmtHours, fmtQty } from '../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_COMPONENT_CATEGORIES, DH_COMPONENT_STATUSES, type DhComponent } from '../../types/downhole';
import { ComponentFormDialog, ImportComponentsDialog } from './ComponentDialogs';
import { DH_SECONDARY, DhBadge, DhCategoryPill, DhEmpty, DhPanel, DhWearMeter } from './DownholeBits';

const isStatus = (v: string | null): v is (typeof DH_COMPONENT_STATUSES)[number] =>
  !!v && (DH_COMPONENT_STATUSES as readonly string[]).includes(v);

/** The connection names, top over bottom. Both absent → `—`. */
function connections(c: DhComponent): string {
  if (!c.connection_top && !c.connection_bottom) return '—';
  return `${c.connection_top || '—'} / ${c.connection_bottom || '—'}`;
}

export default function ComponentsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = canManageDownholeInventory(user?.role);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const status = isStatus(statusParam) ? statusParam : '';
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  // The API searches serial number and description. Debounced so a typed
  // serial is one request, not one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(
    () => ({ ...(status && { status }), ...(category && { category }), ...(q && { q }) }),
    [status, category, q],
  );
  const listQuery = useDownholeComponents(params);
  const dashboardQuery = useDownholeDashboard();
  const alertsQuery = useDownholeComponentAlerts();

  const components = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const unitSystem = meta?.unit_system ?? null;
  const listFailure = queryLoadFailure(listQuery);
  // The write buttons wait for the tenant's unit system: a dialog opened before
  // it arrives would read a metric tenant's millimetres as inches.
  const canWrite = canManage && !!meta;
  const dashboard = dashboardQuery.data?.data ?? null;
  const countsLoading = dashboardQuery.isLoading;
  const alertCount = alertsQuery.data?.data.length;

  const setStatus = (next: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next) nextParams.set('status', next);
    else nextParams.delete('status');
    setSearchParams(nextParams, { replace: true });
  };

  const filtered = !!(status || category || q);
  const openComponent = (id: string) => navigate(`/downhole/components/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openComponent(id);
    }
  };

  const chips: { key: string; label: string; count: number | undefined }[] = [
    { key: '', label: 'All', count: dashboard?.total_components },
    ...DH_COMPONENT_STATUSES.map((s) => ({
      key: s,
      label: dhSkin(DH_COMPONENT_STATUS_SKIN, s).label,
      count: dashboard?.components_by_status?.[s],
    })),
  ];

  return (
    <div data-testid="downhole-components-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Downhole tools"
        title="Components"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canWrite ? (
            <div className="flex flex-wrap gap-2">
              {/* Secondary on the band: white/25 edge, no colour class (CLAUDE.md, the band). */}
              <button
                type="button"
                onClick={() => setImporting(true)}
                className="inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10"
              >
                <FileUp className="size-4" aria-hidden="true" />
                Import CSV
              </button>
              <button type="button" onClick={() => setAdding(true)} className="fi-action fi-action--standard fi-action--primary">
                <Plus className="size-4" aria-hidden="true" />
                Add Component
              </button>
            </div>
          ) : undefined
        }
        stats={[
          {
            icon: <Wrench className="size-4" aria-hidden="true" />,
            label: 'Available',
            sub: 'Free to build with',
            tone: 'success',
            loading: countsLoading,
            value: displayValue(dashboard?.components_by_status?.available),
          },
          {
            icon: <Wrench className="size-4" aria-hidden="true" />,
            label: 'In hole',
            sub: 'Running now',
            tone: 'info',
            loading: countsLoading,
            value: displayValue(dashboard?.components_by_status?.in_hole),
          },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Alerts',
            sub: 'To review',
            tone: 'accent',
            loading: alertsQuery.isLoading,
            value: displayValue(alertCount),
          },
          {
            icon: <Wrench className="size-4" aria-hidden="true" />,
            label: 'Register',
            sub: 'All components',
            tone: 'neutral',
            loading: countsLoading,
            value: displayValue(dashboard?.total_components),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter by status">
            {chips.map((chip) => {
              const active = status === chip.key;
              return (
                <button
                  key={chip.key || 'all'}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setStatus(chip.key)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active
                      ? 'bg-fi-amber text-fi-amber-ink'
                      : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {chip.label}
                  {/* The API's count for the whole register (/dashboard). No
                      number while it is in flight, never a placeholder zero. */}
                  {!countsLoading && (
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                      {displayValue(chip.count)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <label className="relative flex-1">
              <span className="sr-only">Search serial number or description</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search serial number or description"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label="Filter by category"
              className="h-10 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm text-white focus:border-fi-amber focus:outline-none sm:w-56"
            >
              <option value="">All categories</option>
              {DH_COMPONENT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {DH_CATEGORY_LABEL[c]}
                </option>
              ))}
            </select>
          </div>

          <DhPanel
            eyebrow="Tool register"
            title="Components"
            testId="downhole-component-register"
            footer={
              !listQuery.isLoading && !listFailure && components.length > 0
                ? filtered
                  ? `Showing ${components.length} of ${displayValue(dashboard?.total_components)} components`
                  : `${components.length} components`
                : undefined
            }
          >
            {listQuery.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading components" />
              </div>
            ) : listFailure ? (
              <LoadFailureNotice error={queryFailureError(listQuery)} kind={listFailure} subject="components" />
            ) : components.length === 0 ? (
              filtered ? (
                <DhEmpty>No component matches these filters.</DhEmpty>
              ) : (
                <div className="px-4 py-10 text-center" data-testid="downhole-components-empty">
                  <p className="text-sm text-fieldiq-text">There are no components in the register.</p>
                  {canWrite && (
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      <button type="button" className={DH_SECONDARY} onClick={() => setImporting(true)}>
                        <FileUp className="size-4" aria-hidden="true" />
                        Import CSV
                      </button>
                      <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => setAdding(true)}>
                        <Plus className="size-4" aria-hidden="true" />
                        Add Component
                      </button>
                    </div>
                  )}
                </div>
              )
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1080px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Serial #</th>
                        <th scope="col" className="px-4 py-3">Category</th>
                        <th scope="col" className="px-4 py-3 text-right">OD</th>
                        <th scope="col" className="px-4 py-3 text-right">Length</th>
                        <th scope="col" className="px-4 py-3">Connections</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="px-4 py-3 text-right">Runs</th>
                        <th scope="col" className="px-4 py-3 text-right">Run hours</th>
                        <th scope="col" className="px-4 py-3">Wear used</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {components.map((c) => (
                        <tr
                          key={c.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => openComponent(c.id)}
                          onKeyDown={rowKeys(c.id)}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${
                            dhSkin(DH_COMPONENT_STATUS_SKIN, c.status).rail
                          }`}
                        >
                          <td className="px-4 py-3">
                            <p className="whitespace-nowrap font-mono text-sm font-extrabold text-fieldiq-accent-signal">{c.serial_number}</p>
                            <p className="mt-0.5 max-w-[22rem] truncate text-xs text-fieldiq-muted">{c.description || '—'}</p>
                          </td>
                          <td className="px-4 py-3"><DhCategoryPill category={c.category} /></td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{formatDh(c.od, 'diameter', unitSystem)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{formatDh(c.length_ft, 'length', unitSystem)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{connections(c)}</td>
                          <td className="px-4 py-3">
                            <DhBadge map={DH_COMPONENT_STATUS_SKIN} value={c.status} />
                            {c.current_assembly_number && (
                              <p className="mt-1 font-mono text-[0.68rem] text-fieldiq-muted">{c.current_assembly_number}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtQty(c.total_runs)}</td>
                          <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtHours(c.total_run_hours, 1)}</td>
                          <td className="px-4 py-3"><DhWearMeter percent={c.wear_percent_hours} label="Wear used, of the hours limit" /></td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 p-4 md:hidden">
                  {components.map((c) => (
                    <div
                      key={c.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openComponent(c.id)}
                      onKeyDown={rowKeys(c.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">{c.serial_number}</p>
                          <p className="mt-0.5 truncate text-xs text-fieldiq-muted">{c.description || '—'}</p>
                        </div>
                        <DhBadge map={DH_COMPONENT_STATUS_SKIN} value={c.status} />
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-fieldiq-text">
                        <DhCategoryPill category={c.category} />
                        <span>OD {formatDh(c.od, 'diameter', unitSystem)}</span>
                        <span>Run hours {fmtHours(c.total_run_hours, 1)}</span>
                        <DhWearMeter percent={c.wear_percent_hours} label="Wear used, of the hours limit" />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>

      {canManage && (
        <>
          <ComponentFormDialog open={adding} onClose={() => setAdding(false)} units={unitSystem} tz={meta?.time_zone ?? null} />
          <ImportComponentsDialog open={importing} onClose={() => setImporting(false)} units={unitSystem} />
        </>
      )}
    </div>
  );
}
